import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import useRefreshOnFocus from '../useRefreshOnFocus';
import BalanceChart from '../components/BalanceChart';
import { RefreshIcon } from '../components/Icons';

// Money market holdings keyed by account + symbol. Their yield is shown as hover
// text on the position's row rather than as a column.
const mmKey = (accountId, symbol) => `${accountId}:${symbol}`;
const mmTitle = (m, fmt) => [
  `7-day yield ${m.rate_pct.toFixed(2)}% (as of ${m.rate_date})`,
  m.estimated_annual_income != null ? `Estimated annual income ${fmt(m.estimated_annual_income)}` : null,
  `Accrued since ${m.accrued_since} (estimate) ${fmt(m.accrued_estimate)}`,
].filter(Boolean).join('\n');

export default function Dashboard({ onNavigate }) {
  const [positions, setPositions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [prices, setPrices] = useState({});
  const [cashBalances, setCashBalances] = useState({});
  const [moneyMarket, setMoneyMarket] = useState({});
  // Bumped on a full reload so the balance chart refetches; quiet refreshes on
  // tab focus leave the chart (and the user's zoom) alone.
  const [chartRefresh, setChartRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterAccount, setFilterAccount] = useState('');
  const [sortField, setSortField] = useState('symbol');
  const [sortDir, setSortDir] = useState('asc');

  // `silent` refreshes in place without swapping the table for the loading state.
  const load = async (silent = false) => {
    if (!silent) { setLoading(true); setChartRefresh(n => n + 1); }
    const [pos, accs, cash] = await Promise.all([
      api.getPositions(filterAccount || undefined),
      api.getAccounts(),
      api.getCashBalances()
    ]);
    setPositions(pos);
    setAccounts(accs);
    setCashBalances(cash);
    api.getMoneyMarketHoldings()
      .then(rows => setMoneyMarket(Object.fromEntries((rows || []).map(m => [mmKey(m.account_id, m.symbol), m]))))
      .catch(() => {});
    const priceMap = {};
    await Promise.all(pos.map(async p => {
      try { priceMap[p.symbol] = (await api.getLatestPrice(p.symbol)).close_price; } catch {}
    }));
    setPrices(priceMap);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterAccount]);
  useRefreshOnFocus(() => load(true));

  const fmt = (n) => n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtSigned = (n) => (n < 0 ? '-' : '') + fmt(Math.abs(n));
  const fmtPct = (n) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%';

  const enriched = positions.map(p => {
    const cur = prices[p.symbol];
    const value = cur ? cur * p.quantity : null;
    const gain = value != null ? value - p.total_cost : null;
    const pct = gain != null && p.total_cost > 0 ? (gain / p.total_cost) * 100 : null;
    return { ...p, cur, value, gain, pct };
  });

  const sorted = [...enriched].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (av == null) av = sortDir === 'asc' ? Infinity : -Infinity;
    if (bv == null) bv = sortDir === 'asc' ? Infinity : -Infinity;
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortTh = ({ field, label }) => (
    <th onClick={() => toggleSort(field)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const totalCost = enriched.reduce((s, p) => s + p.total_cost, 0);
  const holdingsValue = enriched.reduce((s, p) => s + (p.value ?? p.total_cost), 0);
  const cash = filterAccount
    ? (cashBalances[filterAccount]?.balance ?? 0)
    : Object.values(cashBalances).reduce((s, c) => s + c.balance, 0);
  const totalValue = holdingsValue + cash;
  // Gain/Return are holdings vs. cost; cash has no cost basis.
  const totalGain = holdingsValue - totalCost;
  const totalGainPct = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

  const handleRowClick = (symbol) => {
    if (onNavigate) onNavigate('transactions', { symbol, transaction_type: 'buy' });
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={{ width: 'auto' }} value={filterAccount} onChange={e => setFilterAccount(e.target.value)}>
            <option value="">All Accounts</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button className="icon-btn" title="Refresh" aria-label="Refresh" onClick={() => load()}><RefreshIcon /></button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'stretch' }}>
        {[
          { label: 'Total Value', value: fmtSigned(totalValue), cls: '' },
          { label: 'Cash', value: fmtSigned(cash), cls: cash < 0 ? 'loss' : '' },
          { label: 'Total Cost', value: fmt(totalCost), cls: '' },
          { label: 'Total Gain', value: fmt(totalGain), cls: totalGain >= 0 ? 'gain' : 'loss' },
          { label: 'Return', value: fmtPct(totalGainPct), cls: totalGainPct >= 0 ? 'gain' : 'loss' },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ flex: 1, textAlign: 'right' }}>
            <div className="stat-label">{s.label}</div>
            <div className={`stat-value ${s.cls}`}>{s.value}</div>
          </div>
        ))}
        <div className="stat-card" style={{ width: 100, flexShrink: 0, textAlign: 'right' }}>
          <div className="stat-label">Positions</div>
          <div className="stat-value mono" style={{ fontSize: 16 }}>{positions.length}</div>
        </div>
      </div>

      <BalanceChart accountId={filterAccount} accounts={accounts} onNavigate={onNavigate} refreshKey={chartRefresh} />

      <div className="card">
        <div className="modal-title">Open Positions</div>
        <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
          Click any row to view its transactions
        </div>
        {loading ? <div className="muted" style={{ padding: 32, textAlign: 'center' }}>Loading...</div> : (
          <table>
            <thead>
              <tr>
                <SortTh field="symbol" label="Symbol" />
                <SortTh field="quantity" label="Qty" />
                <SortTh field="avg_cost" label="Avg Cost" />
                <SortTh field="cur" label="Current Price" />
                <SortTh field="value" label="Market Value" />
                <SortTh field="gain" label="Gain/Loss" />
                <SortTh field="pct" label="Return" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => (
                <tr
                  key={i}
                  onClick={() => handleRowClick(p.symbol)}
                  style={{ cursor: 'pointer' }}
                  title={moneyMarket[mmKey(p.account_id, p.symbol)]?.rate_pct != null
                    ? mmTitle(moneyMarket[mmKey(p.account_id, p.symbol)], fmt)
                    : `View ${p.symbol} transactions`}
                >
                  <td><strong className="mono" style={{ color: 'var(--accent)' }}>{p.symbol}</strong></td>
                  <td className="mono amount">{p.quantity}</td>
                  <td className="mono amount">{fmt(p.avg_cost)}</td>
                  <td className="mono amount">{p.cur ? fmt(p.cur) : <span className="muted">—</span>}</td>
                  <td className="mono amount">{p.value ? fmt(p.value) : <span className="muted">—</span>}</td>
                  <td className={`mono amount ${p.gain >= 0 ? 'gain' : 'loss'}`}>{p.gain != null ? fmt(p.gain) : '—'}</td>
                  <td className={`mono amount ${p.pct >= 0 ? 'gain' : 'loss'}`}>{p.pct != null ? fmtPct(p.pct) : '—'}</td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>No open positions yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}